/**
 * PD-35243 — Web Vitals property helpers.
 *
 * PostHog's built-in `$web_vitals` capture (enabled via `capture_performance.web_vitals`)
 * automatically collects LCP, CLS, INP, FID, FCP, and TTFB and attaches the standard
 * PostHog super-properties (`$device_type`, `$current_url`, `$pathname`, etc.) to each
 * event. No manual reporter is needed for normal captures.
 *
 * This module provides:
 *   - `getDeviceType` — derives a canonical device category from the UA string.
 *   - `buildWebVitalsFlushProperties` — assembles the property bag sent when we
 *     manually flush on `pagehide`/`visibilitychange` to ensure INP and CLS
 *     (which are finalized late) are not dropped on mobile during hard navigations.
 */

export type DeviceType = 'phone' | 'tablet' | 'desktop';

/**
 * Derives a canonical device category from the User-Agent string.
 *
 * Uses the same heuristic that PostHog itself applies when it sets `$device_type`
 * so that our manual flush event matches the property value on native `$web_vitals`
 * events for consistent segmentation in insights.
 *
 * @param userAgent - `navigator.userAgent` string (or empty string in SSR)
 * @returns 'phone' | 'tablet' | 'desktop'
 */
export const getDeviceType = (userAgent: string): DeviceType => {
  const ua = userAgent.toLowerCase();

  const isTablet =
    /ipad/.test(ua) || (/android/.test(ua) && !/mobile/.test(ua)) || /tablet/.test(ua);

  if (isTablet) return 'tablet';

  const isMobile = /android.*mobile|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(
    userAgent,
  );

  return isMobile ? 'phone' : 'desktop';
};

export interface WebVitalsFlushProperties {
  $device_type: DeviceType;
  $current_url: string;
  $pathname: string;
  flush_reason: 'pagehide' | 'visibilitychange';
}

/**
 * Builds the property bag attached to the `web_vitals_flush` event that
 * `useWebVitalsFlush` fires on `pagehide` / `visibilitychange`.
 *
 * Keeping property assembly pure (no side-effects, no DOM access) makes
 * it straightforward to unit-test all edge-cases in isolation.
 *
 * @param userAgent - `navigator.userAgent`
 * @param href      - `window.location.href`
 * @param pathname  - `window.location.pathname`
 * @param reason    - Which browser event triggered the flush
 * @returns Property object ready to pass to `safePostHogCapture`
 */
export const buildWebVitalsFlushProperties = (
  userAgent: string,
  href: string,
  pathname: string,
  reason: 'pagehide' | 'visibilitychange',
): WebVitalsFlushProperties => ({
  $device_type: getDeviceType(userAgent),
  $current_url: href,
  $pathname: pathname,
  flush_reason: reason,
});
